/**
 * Copyright 2025 Lumavec Group LLC
 *
 * Commit history panel component - expandable commit history with sync status
 * 
 * For velocity branch: "unpushed" means commits that are on velocity but not on the default branch.
 * The velocity branch shadows the default branch, so unpushed commits represent changes that need
 * to be synced to the default branch (the authoritative source). Sync happens automatically.
 */

import React from 'react';
import { ChevronDown, ChevronRight, Clock, Upload, AlertTriangle, CheckCircle, Circle, Loader, RefreshCw, FileText, FilePlus, FileMinus, FileEdit, X } from 'lucide-react';
import type { CommitHistory, SourceControlProvider } from './types';

export interface CommitFileInfo {
  path: string;
  status: string;
}

interface CommitHistoryPanelProps {
  commitHistory: CommitHistory | null;
  loadingHistory: boolean;
  historyExpanded: boolean;
  onToggleExpanded: () => void;
  onPush: () => void;
  pushing: boolean;
  isSourceControlConfigured: boolean;
  sourceControlProvider: SourceControlProvider;
  /** Target branch name from organization settings (defaults to 'main') */
  defaultBranch?: string;
  /** Currently selected commit hash (for viewing files) */
  selectedCommitHash?: string | null;
  /** Files in the selected commit */
  selectedCommitFiles?: CommitFileInfo[];
  /** Loading state for commit files */
  loadingCommitFiles?: boolean;
  /** Callback when a commit is clicked to view its files */
  onCommitClick?: (commitHash: string) => void;
  /** Callback when a file in the selected commit is clicked */
  onCommitFileClick?: (filePath: string, commitHash: string) => void;
  /** Callback to close the commit files view */
  onCloseCommitFiles?: () => void;
}

/**
 * Format commit date to relative time
 */
function formatCommitDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSecs < 60) {
    return 'just now';
  } else if (diffMins < 60) {
    return `${diffMins}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return date.toLocaleDateString();
  }
}

function getFileStatusIcon(status: string) {
  switch (status) {
    case 'added':
      return <FilePlus size={14} className="text-green-500" />;
    case 'deleted':
      return <FileMinus size={14} className="text-red-500" />;
    case 'modified':
      return <FileEdit size={14} className="text-yellow-500" />;
    case 'renamed':
      return <FileText size={14} className="text-blue-500" />;
    default:
      return <FileText size={14} className="text-gray-500" />;
  }
}

export function CommitHistoryPanel({
  commitHistory,
  loadingHistory,
  historyExpanded,
  onToggleExpanded,
  onPush,
  pushing,
  isSourceControlConfigured,
  sourceControlProvider,
  defaultBranch = 'main',
  selectedCommitHash,
  selectedCommitFiles,
  loadingCommitFiles,
  onCommitClick,
  onCommitFileClick,
  onCloseCommitFiles,
}: CommitHistoryPanelProps) {
  // For velocity branch, unpushed means "not yet on default branch" (will be auto-synced)
  const isVelocityBranch = commitHistory?.branch === 'velocity';
  const syncLabel = isVelocityBranch ? 'to sync' : 'unpushed';
  
  const selectedCommit = selectedCommitHash 
    ? commitHistory?.commits?.find(c => c.hash === selectedCommitHash)
    : null;
  
  return (
    <div className="border-t border-gray-200">
      {/* History Header */}
      <button
        onClick={onToggleExpanded}
        className="w-full p-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {historyExpanded ? (
            <ChevronDown size={16} className="text-gray-500" />
          ) : (
            <ChevronRight size={16} className="text-gray-500" />
          )}
          <Clock size={16} className="text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Commit History</span>
          {commitHistory && commitHistory.unpushedCount > 0 && (
            <span 
              className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                isVelocityBranch 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-orange-100 text-orange-700'
              }`}
              title={isVelocityBranch 
                ? `Commits on velocity that will be auto-synced to ${defaultBranch}` 
                : 'Commits not pushed to remote'}
            >
              {commitHistory.unpushedCount} {syncLabel}
            </span>
          )}
        </div>
        {loadingHistory && (
          <Loader size={14} className="animate-spin text-gray-400" />
        )}
      </button>

      {/* History Content */}
      {historyExpanded && (
        <div className="border-t border-gray-100">
          {/* Sync Info for Velocity Branch */}
          {isVelocityBranch && commitHistory?.hasRemote && commitHistory.unpushedCount > 0 && (
            <div className="p-3 bg-blue-50 border-b border-blue-100">
              <div className="flex items-center gap-2 text-sm text-blue-700">
                <RefreshCw size={14} />
                <span>
                  {commitHistory.unpushedCount} commit{commitHistory.unpushedCount !== 1 ? 's' : ''} ahead of main
                  <span className="text-blue-500 ml-1">(auto-synced)</span>
                </span>
              </div>
            </div>
          )}
          
          {/* Push Button - only show for non-velocity branches */}
          {!isVelocityBranch && commitHistory?.hasRemote && commitHistory.unpushedCount > 0 && (
            <div className="p-3 bg-orange-50 border-b border-orange-100">
              <button
                onClick={onPush}
                disabled={pushing || !isSourceControlConfigured}
                className="w-full btn-primary text-sm py-2 flex items-center justify-center gap-2"
                title={!isSourceControlConfigured ? `${sourceControlProvider === 'github' ? 'GitHub' : 'GitLab'} access token required` : ''}
              >
                {pushing ? (
                  <>
                    <Loader size={14} className="animate-spin" />
                    Pushing...
                  </>
                ) : (
                  <>
                    <Upload size={14} />
                    Push {commitHistory.unpushedCount} commit{commitHistory.unpushedCount !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          )}

          {/* No Remote Warning */}
          {commitHistory && !commitHistory.hasRemote && (
            <div className="p-3 bg-yellow-50 border-b border-yellow-100 text-sm text-yellow-700">
              <AlertTriangle size={14} className="inline mr-1" />
              No remote configured for this repository
            </div>
          )}

          {/* Commits List or Selected Commit Files */}
          <div className="max-h-64 overflow-auto">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <Loader size={20} className="animate-spin text-gray-400" />
              </div>
            ) : selectedCommitHash && selectedCommit ? (
              // Show files for selected commit
              <div>
                {/* Selected commit header */}
                <div className="p-3 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate" title={selectedCommit.message}>
                        {selectedCommit.message}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <span className="font-mono">{selectedCommit.shortHash}</span>
                        <span>•</span>
                        <span>{selectedCommit.author.name}</span>
                        <span>•</span>
                        <span>{formatCommitDate(selectedCommit.date)}</span>
                      </div>
                    </div>
                    <button
                      onClick={onCloseCommitFiles}
                      className="ml-2 p-1 hover:bg-gray-200 rounded transition-colors"
                      title="Back to commit list"
                    >
                      <X size={16} className="text-gray-500" />
                    </button>
                  </div>
                </div>
                
                {/* Files in commit */}
                {loadingCommitFiles ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader size={20} className="animate-spin text-gray-400" />
                  </div>
                ) : selectedCommitFiles && selectedCommitFiles.length > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {selectedCommitFiles.map((file) => (
                      <button
                        key={file.path}
                        onClick={() => onCommitFileClick?.(file.path, selectedCommitHash)}
                        className="w-full p-2 px-3 flex items-center gap-2 hover:bg-blue-50 transition-colors text-left"
                      >
                        {getFileStatusIcon(file.status)}
                        <span className="text-sm text-gray-700 truncate flex-1" title={file.path}>
                          {file.path}
                        </span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          file.status === 'added' ? 'bg-green-100 text-green-700' :
                          file.status === 'deleted' ? 'bg-red-100 text-red-700' :
                          file.status === 'modified' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {file.status}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center text-sm text-gray-500">
                    No files changed in this commit
                  </div>
                )}
              </div>
            ) : commitHistory?.commits && commitHistory.commits.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {commitHistory.commits.map((commit) => (
                  <button
                    key={commit.hash}
                    onClick={() => onCommitClick?.(commit.hash)}
                    className={`w-full p-3 text-left hover:bg-gray-50 transition-colors ${
                      selectedCommitHash === commit.hash ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {/* Sync status icon - for velocity branch, this indicates if commit is on main */}
                      {commitHistory.hasRemote ? (
                        commit.pushed ? (
                          <span title={isVelocityBranch ? `Synced to ${defaultBranch}` : "Pushed to remote"}>
                            <CheckCircle size={14} className="mt-0.5 text-green-500 flex-shrink-0" />
                          </span>
                        ) : (
                          <span title={isVelocityBranch ? "Not yet on main (will auto-sync)" : "Not pushed"}>
                            <Circle size={14} className={`mt-0.5 flex-shrink-0 ${isVelocityBranch ? 'text-blue-500' : 'text-orange-500'}`} />
                          </span>
                        )
                      ) : (
                        <span title="No remote">
                          <Circle size={14} className="mt-0.5 text-gray-300 flex-shrink-0" />
                        </span>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-900 truncate" title={commit.message}>
                          {commit.message}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                          <span className="font-mono">{commit.shortHash}</span>
                          <span>•</span>
                          <span title={commit.author.email}>{commit.author.name}</span>
                          <span>•</span>
                          <span title={commit.date}>
                            {formatCommitDate(commit.date)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-sm text-gray-500">
                No commits yet
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

